import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Briefcase, Zap, Users, Cpu, HeartHandshake, MapPin, Copy, Check, AtSign } from "lucide-react";
import { toast } from "sonner";
import SectionHead from "./SectionHead";
import { fadeUp, stagger, sectionPad } from "./motion";
import { useLang } from "../../i18n/LanguageContext";

function CopyButton({ text, label, copiedLabel, failLabel, tid, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error(failLabel);
    }
  };
  return (
    <button
      onClick={copy}
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full border border-vg-green/50 px-5 py-2 font-display text-xs font-semibold uppercase tracking-wider text-vg-green transition-all duration-200 hover:bg-vg-green hover:text-vg-black"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? copiedLabel : label}
      {tid && <span className="sr-only">{tid}</span>}
    </button>
  );
}

export default function Social() {
  const { t, lang } = useLang();
  const c = t.social;
  const [active, setActive] = useState(0);
  const [postLang, setPostLang] = useState(lang);

  useEffect(() => {
    setPostLang(lang);
  }, [lang]);

  const post = c.posts[active];

  return (
    <section id="social" className={`relative bg-vg-card ${sectionPad}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead index="04" kicker={c.kicker} title={c.title} en={c.en} />

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative mb-12 overflow-hidden border border-white/10"
          data-testid="social-team-banner"
        >
          <img src="/img/team104.jpg" alt="Un team EVGP con la propria monoposto" className="h-[260px] w-full object-cover object-[50%_72%] sm:h-[360px]" />
          <div className="absolute inset-0 bg-gradient-to-t from-vg-black via-vg-black/30 to-transparent" aria-hidden="true" />
          <div className="absolute bottom-6 left-6 sm:bottom-8 sm:left-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-vg-green">{c.bannerTag}</p>
            <p className="mt-2 font-display text-2xl font-bold uppercase tracking-wide text-vg-white sm:text-3xl">{c.bannerTitle}</p>
          </div>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="grid gap-5 lg:grid-cols-3">
          {c.channels.map((ch) => (
            <motion.article key={ch.name} variants={fadeUp} className="border border-white/10 bg-vg-black p-8" data-testid={`canale-${ch.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              {ch.name.includes("Instagram") ? <Zap className="h-8 w-8 text-vg-green" /> : ch.name.includes("LinkedIn") ? <Briefcase className="h-8 w-8 text-vg-green" /> : <Users className="h-8 w-8 text-vg-green" />}
              <h3 className="mt-5 font-display text-2xl font-semibold uppercase tracking-wider text-vg-white">{ch.name}</h3>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-green">{ch.audience}</p>
              <p className="mt-4 text-sm leading-relaxed text-vg-muted">{ch.desc}</p>
            </motion.article>
          ))}
        </motion.div>

        <div className="mt-16">
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7 }}
            className="font-display text-3xl font-bold uppercase tracking-wide text-vg-white sm:text-4xl"
          >
            {c.calTitle}
          </motion.h3>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.15 }} variants={stagger} className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {c.formats.map((f) => (
              <motion.article key={f.tag} variants={fadeUp} className="group relative border border-white/10 bg-vg-asphalt p-7 transition-colors duration-300 hover:border-vg-green/40" data-testid={`format-${f.tag.replace("#", "").toLowerCase()}`}>
                {f.tag === "#TechTuesday" ? <Cpu className="h-6 w-6 text-vg-green" /> : f.tag === "#MeetTheTeam" ? <Users className="h-6 w-6 text-vg-green" /> : f.tag === "#RoadToMisano" ? <MapPin className="h-6 w-6 text-vg-green" /> : <HeartHandshake className="h-6 w-6 text-vg-green" />}
                <p className="mt-5 font-display text-2xl font-semibold uppercase tracking-wider text-vg-white">{f.tag}</p>
                <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-green">{f.cadence}</p>
                <p className="mt-3 text-sm leading-relaxed text-vg-muted">{f.desc}</p>
              </motion.article>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="mt-16 border border-white/10 bg-vg-black"
          data-testid="channel-setup-section"
        >
          <div className="border-b border-white/10 p-6 sm:p-8">
            <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-vg-white sm:text-3xl">{c.setupTitle}</h3>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-muted">{c.setupSub}</p>
          </div>
          <div className="grid gap-5 p-6 sm:p-8 lg:grid-cols-3">
            {c.channelsSetup.map((s) => (
              <article key={s.name} className="border border-white/10 bg-vg-card p-6" data-testid={`setup-${s.name.toLowerCase()}`}>
                <div className="flex items-center justify-between">
                  <h4 className="font-display text-xl font-semibold uppercase tracking-wider text-vg-white">{s.name}</h4>
                  <AtSign className="h-4 w-4 text-vg-green" />
                </div>
                <p className="mt-3 border border-white/10 bg-vg-black px-3 py-2 font-mono text-xs text-vg-green">{s.handle}</p>
                {["it", "en"].map((l) => (
                  <div key={l} className="mt-4">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-vg-muted">{l === "it" ? "Bio ITA" : "Bio ENG"}</span>
                      <CopyButton
                        text={l === "it" ? s.bioIt : s.bioEn}
                        label={c.copyPost}
                        copiedLabel={c.copied}
                        failLabel={c.copyFailed}
                        testid={`copy-bio-${s.name.toLowerCase()}-${l}`}
                      />
                    </div>
                    <p className="border border-white/10 bg-vg-black p-3 text-xs leading-relaxed text-vg-white">{l === "it" ? s.bioIt : s.bioEn}</p>
                  </div>
                ))}
              </article>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8 }}
          className="mt-12 border border-white/10 bg-vg-black"
          data-testid="copywriting-section"
        >
          <div className="flex flex-col gap-4 border-b border-white/10 p-6 sm:flex-row sm:items-center sm:justify-between sm:p-8">
            <div>
              <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-vg-white sm:text-3xl">{c.copyTitle}</h3>
              <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.22em] text-vg-muted">{c.copySub}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex overflow-hidden rounded-full border border-white/15" data-testid="post-lang-toggle">
                {["it", "en"].map((l) => (
                  <button
                    key={l}
                    onClick={() => setPostLang(l)}
                    data-testid={`post-lang-${l}`}
                    aria-pressed={postLang === l}
                    className={`px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-200 ${
                      postLang === l ? "bg-vg-green text-vg-black" : "text-vg-muted hover:text-vg-white"
                    }`}
                  >
                    {l === "it" ? "ITA" : "ENG"}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="p-6 sm:p-8">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-vg-green">{post.audience[postLang]}</p>
              <div className="flex flex-wrap gap-2" data-testid="post-tabs">
                {c.posts.map((p, i) => (
                  <button
                    key={p.id}
                    onClick={() => setActive(i)}
                    data-testid={`post-tab-${p.id}`}
                    aria-pressed={active === i}
                    className={`rounded-full border px-4 py-2 font-display text-xs font-semibold uppercase tracking-wider transition-all duration-200 ${
                      active === i ? "border-vg-green bg-vg-green text-vg-black" : "border-white/15 text-vg-muted hover:border-vg-green/50 hover:text-vg-white"
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-white/10 bg-vg-card p-6 sm:p-8">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-vg-white sm:text-base" data-testid={`post-content-${post.id}-${postLang}`}>
                {post.text[postLang]}
              </pre>
            </div>
            <div className="mt-5">
              <CopyButton
                text={post.text[postLang]}
                label={c.copyPost}
                copiedLabel={c.copied}
                failLabel={c.copyFailed}
                testid="copy-post-button"
              />
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
